import countries from '@trm/shared/data/countries';
import { NativeSelect, Select } from '@trm/ui-kit-admin/v7';
import { useBreakpoint } from '@trm/ui-kit-admin/v7/hooks';
import { cx } from '@trm/ui-kit-admin/v7/utils';
import React, { useMemo } from 'react';
import '~/pages/auth/auth-shared.css';
import '~/pages/auth/onboarding/components/OnboardingInput/OnboardingInput.css';
import { getFlagUrl } from '~/utils/flagImageUtils';

interface FormCountrySelectProps {
  label?: string;
  name?: string;
  placeholder: string;
  value: string;
  onChange: (field: string, value: string) => void;
  onBlur?: (e: React.FocusEvent<Element>) => void;
  error?: string;
  touched?: boolean;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  popoverClassName?: string;
}

/**
 * Country selector that updates the `name` field with the selected ISO country
 * code and clears `region` when the country changes.
 *
 * Designed for Formik-style forms where `onChange(field, value)` updates form state.
 */
const FormCountrySelect: React.FC<FormCountrySelectProps> = ({
  label,
  name = 'country',
  placeholder,
  value,
  onChange,
  onBlur,
  error,
  touched,
  disabled = false,
  className,
  inputClassName,
  popoverClassName,
}) => {
  const isDesktop = useBreakpoint('md');

  const handleSelectionChange = (key: React.Key | null) => {
    onChange(name, key ? String(key) : '');
    onChange('region', '');
  };

  const countryItems = useMemo(() => {
    return countries.map((c) => ({
      id: c.code,
      label: c.label,
      supportingText: `(${c.code}) ${c.phone}`,
      avatarUrl: getFlagUrl(c.code),
    }));
  }, []);

  const handleBlur = (e: React.FocusEvent<Element>) => {
    if (onBlur) {
      Object.defineProperty(e.target, 'name', {
        writable: true,
        value: name,
      });
      onBlur(e);
    }
  };

  // Use native select on mobile for better scrolling UX
  if (!isDesktop) {
    const nativeOptions = [
      { label: placeholder, value: '' },
      ...countries.map((c) => ({
        label: `${c.label} (${c.code})`,
        value: c.code,
      })),
    ];

    return (
      <NativeSelect
        label={label}
        value={value}
        onChange={(e) => handleSelectionChange(e.target.value)}
        onBlur={handleBlur}
        disabled={disabled}
        aria-invalid={!!(error && touched)}
        options={nativeOptions}
        hint={touched && error ? error : undefined}
        className={className}
        selectClassName={cx(
          '!rounded-[2px] !bg-[var(--auth-bg-primary-subtle)] !ring-0 !border !border-disabled',
          inputClassName,
        )}
      />
    );
  }

  return (
    <div className="onboarding-select-wrapper">
      <Select.ComboBox
        label={label}
        placeholder={placeholder}
        selectedKey={value || null}
        onSelectionChange={handleSelectionChange}
        onBlur={handleBlur}
        isDisabled={disabled}
        isInvalid={!!(error && touched)}
        hint={touched && error ? error : undefined}
        size="md"
        shortcut={false}
        inputClassName={cx(
          '!rounded-[2px] bg-[var(--auth-bg-primary-subtle)] !ring-0 border border-disabled',
          inputClassName,
        )}
        popoverClassName={cx(popoverClassName)}
        className={className}
        items={countryItems}
      >
        {(item) => (
          <Select.Item id={item.id} avatarUrl={item.avatarUrl} supportingText={item.supportingText}>
            {item.label}
          </Select.Item>
        )}
      </Select.ComboBox>
    </div>
  );
};

export default FormCountrySelect;
